import React, { useState } from 'react';
import {
  Box, Button, ButtonBase, Card, Chip, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import RepeatIcon from '@mui/icons-material/Repeat';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { CardGrid, Loader, NeedsHousehold, PageHeader, WeekNavigator, useHousehold, useLoad } from '../common';
import { addDays, dayLabel, dishesText, mealWarning, slotSubtitle, slotTitle, today, weekStart, weekTitle } from '../labels';
import { PLAN_PATH, planUrl, useWeekParam } from './shared';
import { SoupRow } from './SoupRow';
import { MealConfirmQuestion } from './MealConfirm';
import { SettleDialog } from './SettleDialog';

interface DayMeals { date: T.DateOnly; lunch?: T.MealSlot; dinner?: T.MealSlot; }

function days(plan: T.WeekPlan): DayMeals[] {
  const byDate: Record<string, DayMeals> = {};
  plan.slots.forEach(s => {
    const day = byDate[s.date] ?? (byDate[s.date] = { date: s.date });
    if (s.mealType === 'Lunch') day.lunch = s; else day.dinner = s;
  });
  return Object.keys(byDate).sort().map(k => byDate[k]);
}

function SlotRow({ label, slot, plan, members, onAnswered, onSettled }: {
  label: string; slot?: T.MealSlot; plan: T.WeekPlan; members: T.HouseholdMember[]; onAnswered: (week: T.WeekPlan) => void; onSettled: () => void;
}) {
  if (!slot) return null;
  const week = plan.weekStart.substring(0, 10);
  return (
    <>
    <ButtonBase
      component={RouterLink}
      to={`${PLAN_PATH}/refeicao/${slot.publicId}?semana=${week}`}
      sx={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'flex-start', gap: 1, px: 1.75, py: 1.1, minHeight: 64, '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ width: 58, flexShrink: 0, lineHeight: '22px', fontSize: 10.5 }}>{label}</Typography>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography
          variant="body1"
          sx={slot.kind === 'Empty'
            ? { color: 'text.disabled', fontStyle: 'italic', fontSize: 15 }
            : { color: 'text.primary', overflowWrap: 'anywhere', fontWeight: 600, fontSize: 15, lineHeight: 1.35 }}
        >
          {slot.kind === 'Empty' ? 'Por planear' : slotTitle(slot)}
          {slot.isLocked && <LockIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} titleAccess="Bloqueada" />}
        </Typography>
        {slot.dishes.length > 0 && (
          <Typography variant="body2" component="div" sx={{ overflowWrap: 'anywhere' }}>{dishesText(slot.dishes)}</Typography>
        )}
        <Typography variant="caption" color="text.secondary" component="div">{slotSubtitle(slot, members)}</Typography>
        {slot.warnings.length > 0 && (
          <Typography variant="caption" component="div" sx={{ color: 'warning.dark', whiteSpace: 'pre-line' }}>
            {slot.warnings.map(mealWarning).join('\n')}
          </Typography>
        )}
        {slot.didNotHappen && (
          <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: 600 }}>Não comemos</Typography>
        )}
      </Box>
    </ButtonBase>
    {slot.needsConfirmation && <MealConfirmQuestion slot={slot} week={plan} onAnswered={onAnswered} onSettled={onSettled} />}
    </>
  );
}

function PlanContent() {
  const navigate = useNavigate();
  const week = useWeekParam();
  const { members } = useHousehold();
  const { data, setData, error, loading, reload } = useLoad(() => api.getWeekPlan(week), [week]);
  const pots = useLoad(() => api.getPotWeek(week), [week]);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [settling, setSettling] = useState<T.Guid[] | null>(null);
  const thisWeek = weekStart(today());
  const shown = data && data.weekStart.substring(0, 10) === week ? data : undefined;

  const go = (to: string) => { setMenu(null); navigate(to); };
  const pending = shown ? shown.slots.filter(sl => sl.needsConfirmation) : [];

  return (
    <>
      <PageHeader
        eyebrow="Comida"
        title="Plano da semana"
        subtitle={shown ? `${shown.slots.filter(sl => sl.kind !== 'Empty').length} de ${shown.slots.length} refeições planeadas` : undefined}
        actions={<>
          <WeekNavigator
            title={weekTitle(week)}
            isCurrent={week === thisWeek}
            onPrevious={() => navigate(planUrl(addDays(week, -7)))}
            onNext={() => navigate(planUrl(addDays(week, 7)))}
            onToday={() => navigate(planUrl(thisWeek))}
          />
          <Button variant="outlined" startIcon={<ShoppingCartIcon />} component={RouterLink} to={`/casa/compras/da-ementa?semana=${week}`} sx={{ display: { xs: 'none', lg: 'inline-flex' } }}>
            Lista da ementa
          </Button>
          <Button variant="contained" startIcon={<AutoAwesomeIcon />} component={RouterLink} to={`${PLAN_PATH}/sugerir?semana=${week}`}>
            Sugerir
          </Button>
          <IconButton aria-label="Mais opções" onClick={e => setMenu(e.currentTarget)}><MoreVertIcon /></IconButton>
        </>}
      />
      <Menu anchorEl={menu} open={menu !== null} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => go(`${PLAN_PATH}/sugerir?semana=${week}`)}>
          <ListItemIcon><AutoAwesomeIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Sugerir a semana" secondary="Propõe receitas para as refeições por definir" />
        </MenuItem>
        <MenuItem onClick={() => go(`/casa/compras/da-ementa?semana=${week}`)}>
          <ListItemIcon><ShoppingCartIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Lista de compras da ementa" secondary="O que falta para as refeições desta semana" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => go(`${PLAN_PATH}/regras`)}>
          <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Regras das sugestões" secondary="Repetições, peixe, rápidas ao jantar…" />
        </MenuItem>
        <MenuItem onClick={() => go(`${PLAN_PATH}/padroes`)}>
          <ListItemIcon><RepeatIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Padrões das refeições" secondary="Quem come em cada refeição da semana" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setMenu(null); reload(); pots.reload(); }}>
          <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Atualizar" />
        </MenuItem>
      </Menu>

      <Loader loading={loading} error={error} onRetry={reload} hasData={shown !== undefined}>
        {shown && pots.data && pots.data.weekStart.substring(0, 10) === week && (
          <Box sx={{ mb: 2 }}><SoupRow week={pots.data} onChanged={pots.reload} /></Box>
        )}
        {pending.length > 0 && (
          <Card sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1, mb: 2 }}>
            <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>
              {pending.length === 1 ? '1 refeição por efetivar' : `${pending.length} refeições por efetivar`}
            </Typography>
            <Button variant="contained" size="small" onClick={() => setSettling(pending.map(sl => sl.publicId))}>Efetivar</Button>
          </Card>
        )}
        {settling && <SettleDialog slotIds={settling} onClose={changed => { setSettling(null); if (changed) reload(); }} />}
        {shown && (
          <CardGrid min={250}>
            {days(shown).map(day => {
              const isToday = day.date === today();
              const [weekday, date] = dayLabel(day.date).split(', ');
              return (
                <Card
                  key={day.date}
                  sx={theme => ({
                    opacity: day.date < today() ? 0.72 : 1, '&:hover': { opacity: 1 },
                    ...(isToday ? { borderColor: 'primary.main', boxShadow: `0 0 0 1px ${theme.palette.primary.main}` } : {}),
                  })}
                >
                  <Box sx={theme => ({ display: 'flex', alignItems: 'baseline', gap: 1, px: 1.75, pt: 1.5, pb: 1, bgcolor: isToday ? alpha(theme.palette.primary.main, 0.07) : undefined })}>
                    <Typography sx={{ fontWeight: 700, color: isToday ? 'primary.dark' : undefined }}>{weekday.replace(/-feira$/i, '')}</Typography>
                    <Typography variant="body2" color="text.secondary">{date}</Typography>
                    {isToday && <Chip size="small" color="primary" label="Hoje" sx={{ ml: 'auto', height: 22 }} />}
                  </Box>
                  <SlotRow label="Almoço" slot={day.lunch} plan={shown} members={members} onAnswered={setData} onSettled={reload} />
                  <Divider sx={{ mx: 1.75 }} />
                  <SlotRow label="Jantar" slot={day.dinner} plan={shown} members={members} onAnswered={setData} onSettled={reload} />
                  <Box sx={{ pb: 0.5 }} />
                </Card>
              );
            })}
          </CardGrid>
        )}
      </Loader>
    </>
  );
}

/** The week's meal plan: lunch and dinner for each day. */
const MealPlanPage: React.FC = () => <NeedsHousehold><PlanContent /></NeedsHousehold>;

export default MealPlanPage;
